/**
 * Interpreter controls for the two-stage inbound hearing flow.
 *
 * The interpreter answers the held hearing caller first, then rings the Deaf
 * client already bound to the platform call. The server resolves that target
 * from the call record — there is no picker and no way to aim elsewhere.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { appNavigate } from '../../app/actions';
import { twilioVoiceService } from '../../interpreter-phone/services/TwilioVoiceService';
import { queueService } from '../../interpreter-queue/InterpreterQueueService';
import { recordingStarted } from '../../voicemail/actions';
import { startVoicemailRecording } from '../../voicemail/functions';
import { getPersistentJson, removePersistentItem, setPersistentItem } from '../../vrs-auth/storage';
const RERING_COOLDOWN_MS = 15000;
const useStyles = makeStyles()(() => ({
    banner: {
        position: 'fixed',
        top: '18px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 380,
        alignItems: 'center',
        background: 'rgba(10, 14, 24, 0.88)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '12px',
        color: '#fff',
        display: 'flex',
        gap: '12px',
        padding: '10px 16px',
        backdropFilter: 'blur(8px)'
    },
    text: { fontSize: '13px', fontWeight: 600, opacity: 0.9 },
    ringButton: {
        background: '#2563eb',
        border: 'none',
        borderRadius: '8px',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 700,
        padding: '8px 14px',
        whiteSpace: 'nowrap',
        '&:disabled': { cursor: 'default', opacity: 0.55 }
    },
    voicemailButton: {
        background: 'transparent',
        border: '1px solid rgba(255, 255, 255, 0.35)',
        borderRadius: '8px',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 700,
        padding: '8px 14px',
        whiteSpace: 'nowrap',
        '&:disabled': { cursor: 'default', opacity: 0.55 }
    },
    endButton: {
        background: '#991b1b',
        border: '1px solid #dc2626',
        borderRadius: '8px',
        color: '#fff',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: 700,
        padding: '8px 14px',
        whiteSpace: 'nowrap',
        '&:disabled': { cursor: 'default', opacity: 0.55 }
    }
}));
export default function RingClientBanner({ clientMissing }) {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const matchData = useSelector((state) => state['features/interpreter-queue']?.matchData);
    const conferenceRoom = useSelector((state) => state['features/base/conference']?.room);
    // Same resolution as ShiftPanel: redux when the match happened in this
    // page load, otherwise the call the dashboard persisted before navigating.
    // The client identity rides along only to address the VideoMail draft —
    // the re-ring itself sends nothing but the call id.
    const activeCall = useMemo(() => {
        if (matchData?.callId
            && (!conferenceRoom || matchData.roomName === conferenceRoom)) {
            return {
                callId: matchData.callId,
                calleeId: matchData.clientId,
                calleeName: matchData.clientName,
                requestKind: matchData.requestKind,
                roomName: matchData.roomName
            };
        }
        const stored = getPersistentJson('vrs_active_call');
        if (stored?.callId && (!conferenceRoom || stored.roomName === conferenceRoom)) {
            return {
                callId: stored.callId,
                calleeId: stored.calleeId || stored.clientId,
                calleeName: stored.calleeName || stored.clientName,
                requestKind: stored.requestKind,
                roomName: stored.roomName,
                videoMailRelay: stored.videoMailRelay
            };
        }
        return undefined;
    }, [conferenceRoom, matchData]);
    const activeCallId = activeCall?.callId;
    const endOwner = useMemo(() => ({ callId: activeCallId, roomName: conferenceRoom }), [activeCallId, conferenceRoom]);
    const currentEndOwner = useRef(endOwner);
    currentEndOwner.current = endOwner;
    const pendingEnd = useRef(null);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);
    const isInboundHearing = activeCall?.requestKind === 'inbound_hearing';
    const [status, setStatus] = useState('');
    const [lastRingAt, setLastRingAt] = useState(0);
    const [now, setNow] = useState(Date.now());
    const [ringSent, setRingSent] = useState(false);
    const [ending, setEnding] = useState(false);
    useEffect(() => { setEnding(false); setStatus(''); }, [endOwner]);
    const [phoneStatus, setPhoneStatus] = useState(() => twilioVoiceService.getCallStatus());
    const coolingDown = now - lastRingAt < RERING_COOLDOWN_MS;
    useEffect(() => {
        if (!coolingDown) {
            return;
        }
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [coolingDown]);
    useEffect(() => {
        const onSent = () => setStatus('Ring sent — waiting for the client to join…');
        const onSkipped = (payload) => {
            const message = payload?.message;
            setStatus(typeof message === 'string' && message ? message : 'The client already has this call open.');
        };
        const onPhoneStatus = (nextStatus) => setPhoneStatus(nextStatus);
        const onError = (payload) => {
            if (payload?.code === 'CALL_ENDED' || payload?.code === 'FORBIDDEN' || payload?.code === 'NO_CLIENT') {
                setStatus(payload.message || 'Could not ring the client.');
            }
        };
        queueService.on('clientReringSent', onSent);
        queueService.on('clientReringSkipped', onSkipped);
        queueService.on('error', onError);
        twilioVoiceService.on('statusChange', onPhoneStatus);
        return () => {
            queueService.off('clientReringSent', onSent);
            queueService.off('clientReringSkipped', onSkipped);
            queueService.off('error', onError);
            twilioVoiceService.off('statusChange', onPhoneStatus);
        };
    }, []);
    const onRing = useCallback(() => {
        if (!activeCallId || (isInboundHearing && phoneStatus.status !== 'connected')) {
            return;
        }
        queueService.ringClientAgain(activeCallId);
        setRingSent(true);
        setLastRingAt(Date.now());
        setNow(Date.now());
        setStatus('Ringing client…');
    }, [activeCallId, isInboundHearing, phoneStatus.status]);
    // Same flow as VoicemailPrompt: mint the draft, then hop to the dedicated
    // recording room where the middleware auto-starts the Jibri recording.
    // Preserve the hearing leg through this intentional room navigation so
    // the terp can hear the spoken message while signing it for VideoMail.
    const onLeaveVoicemail = useCallback(async () => {
        if (!activeCall?.calleeId || (isInboundHearing && phoneStatus.status !== 'connected')) {
            return;
        }
        let storedBeforeNavigation = null;
        let preserveArmed = false;
        try {
            const result = await startVoicemailRecording({ calleeId: activeCall.calleeId });
            dispatch(recordingStarted({
                calleeId: activeCall.calleeId,
                calleeName: activeCall.calleeName,
                messageId: result.messageId,
                roomName: result.roomName,
                maxDurationSeconds: result.maxDurationSeconds
            }));
            if (isInboundHearing) {
                storedBeforeNavigation = getPersistentJson('vrs_active_call');
                setPersistentItem('vrs_active_call', JSON.stringify({
                    ...(storedBeforeNavigation || {}),
                    callId: activeCall.callId,
                    clientId: activeCall.calleeId,
                    clientName: activeCall.calleeName,
                    requestKind: activeCall.requestKind,
                    roomName: result.roomName,
                    videoMailRelay: true
                }));
                twilioVoiceService.preserveCurrentCallForNavigation();
                preserveArmed = true;
            }
            try {
                const navigationSucceeded = await Promise.resolve(dispatch(appNavigate(result.roomName, {
                    hidePrejoin: true,
                    startWithAudioMuted: false,
                    startWithVideoMuted: false
                })));
                if (navigationSucceeded === false) {
                    throw new Error('Could not join the VideoMail room.');
                }
            }
            catch (error) {
                if (isInboundHearing) {
                    if (storedBeforeNavigation) {
                        setPersistentItem('vrs_active_call', JSON.stringify(storedBeforeNavigation));
                    }
                    else {
                        removePersistentItem('vrs_active_call');
                    }
                }
                throw error;
            }
        }
        catch (error) {
            if (preserveArmed) {
                twilioVoiceService.cancelPreserveCurrentCallForNavigation();
            }
            setStatus(error instanceof Error ? error.message : 'Could not start VideoMail.');
        }
    }, [activeCall, dispatch, isInboundHearing, phoneStatus.status]);
    const onEndCall = useCallback(async () => {
        if (!activeCallId || pendingEnd.current === endOwner)
            return;
        pendingEnd.current = endOwner;
        setEnding(true);
        setStatus('Ending call…');
        try {
            await twilioVoiceService.hangupCall();
            if (mounted.current && currentEndOwner.current === endOwner)
                queueService.endActiveCall();
        }
        catch (error) {
            if (mounted.current && currentEndOwner.current === endOwner) {
                setStatus(error instanceof Error ? error.message : 'Phone hangup could not be confirmed. Retry End.');
            }
        }
        finally {
            if (pendingEnd.current === endOwner)
                pendingEnd.current = null;
            if (mounted.current && currentEndOwner.current === endOwner)
                setEnding(false);
        }
    }, [activeCallId, endOwner]);
    if (!clientMissing || !activeCallId || (isInboundHearing && activeCall.videoMailRelay)) {
        return null;
    }
    return (React.createElement("div", { className: classes.banner },
        React.createElement("span", { className: classes.text }, status || (isInboundHearing
            ? phoneStatus.status === 'connected'
                ? `Hearing caller connected. Ring ${activeCall?.calleeName || 'the Deaf client'} when ready.`
                : phoneStatus.status === 'failed'
                    ? phoneStatus.error || 'Could not answer the hearing caller.'
                    : 'Answering the hearing caller…'
            : 'The client hasn\'t joined yet.')),
        React.createElement("button", { className: classes.ringButton, disabled: coolingDown || ending || (isInboundHearing && phoneStatus.status !== 'connected'), onClick: onRing, type: 'button' }, coolingDown
            ? `Ring again in ${Math.ceil((RERING_COOLDOWN_MS - (now - lastRingAt)) / 1000)}s`
            : ringSent
                ? 'Ring again'
                : `Ring ${activeCall?.calleeName || 'client'}`),
        activeCall?.calleeId && (React.createElement("button", { className: classes.voicemailButton, disabled: ending || (isInboundHearing && phoneStatus.status !== 'connected'), onClick: onLeaveVoicemail, title: 'Record a signed VideoMail for this client instead of ringing again', type: 'button' }, "Leave VideoMail")),
        isInboundHearing && (React.createElement("button", { className: classes.endButton, disabled: ending, onClick: onEndCall, type: 'button' }, ending ? 'Ending…' : 'End call'))));
}
